"use client";
import { useRef } from "react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useScrollContainer } from "@/contexts/layout";
import { useViewportMetrics } from "@/hooks/dom";
import { useLandingDeferredSectionVisibility } from "./useLandingDeferredSectionVisibility";
const LANDING_SECTION_CONTENT_ENTER_DURATION_SEC = 0.45;
const LANDING_SECTION_CONTENT_EXIT_DURATION_SEC = 0.2;
const LANDING_SECTION_CONTENT_ENTER_Y = 10;
interface LandingDeferredSectionProps {
    children: (sectionRef: RefObject<HTMLElement | null>) => ReactNode;
    name: string;
    style?: CSSProperties;
}
export function LandingDeferredSection({ children, name, style, }: LandingDeferredSectionProps): ReactNode {
    const sectionRef = useRef<HTMLElement | null>(null);
    const { containerRef } = useScrollContainer();
    const { vh } = useViewportMetrics();
    const { hasEnteredView, isInView } = useLandingDeferredSectionVisibility({
        containerRef,
        sectionRef,
    });
    const sectionStyle: CSSProperties = {
        position: "relative",
        width: "100%",
        height: vh * 100,
        minHeight: vh * 100,
        maxHeight: vh * 100,
        overflow: "hidden",
        backgroundColor: "#000000",
        scrollSnapAlign: "start",
        scrollSnapStop: "always",
        ...style,
    };
    return (
      <section ref={sectionRef} style={sectionStyle}>
        <AnimatePresence initial={false}>
          {hasEnteredView && isInView && (<motion.div key={`${name}Content`} initial={{
                opacity: 0,
                y: LANDING_SECTION_CONTENT_ENTER_Y,
            }} animate={{
                opacity: 1,
                y: 0,
            }} exit={{
                opacity: 0,
                y: LANDING_SECTION_CONTENT_ENTER_Y,
                transition: {
                    duration: LANDING_SECTION_CONTENT_EXIT_DURATION_SEC,
                    ease: [0.4, 0, 0.2, 1],
                },
            }} transition={{
                duration: LANDING_SECTION_CONTENT_ENTER_DURATION_SEC,
                ease: [0.4, 0, 0.2, 1],
            }} style={{
                position: "relative",
                width: "100%",
                height: "100%",
                willChange: "opacity, transform",
            }}>
              {children(sectionRef)}
            </motion.div>)}
        </AnimatePresence>
      </section>
    );
}
